// src/pages/organization/tabs/WarehousesTab.jsx
import { useState } from 'react';
import {
  useListLocations,
  useCreateLocation,
  useUpdateLocation,
  useArchiveLocation,
  useUnarchiveLocation,
} from '../../../features/warehouses/api';
import { Modal } from '../../../components/Modal/Modal';
import OperatingHoursEditor from '../../../components/OperatingHoursEditor/OperatingHoursEditor';
import { coerceOperatingHours, OperatingHours } from '../../../features/addresses/operatingHours';
import { toast } from 'react-toastify';

const EMPTY = {
  name: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'US',
  deliveryFee: '0',
  pickupInstructions: '',
  operatingHours: null as OperatingHours | null,
};

function toCents(value: unknown) {
  if (value === '' || value == null) return 0;
  const n = Number(value);
  if (!isFinite(n) || n < 0) return 0;
  return Math.round(n * 100);
}

function fmtDollarsFromCents(cents: unknown) {
  const n = Number(cents || 0) / 100;
  if (!isFinite(n) || n <= 0) return '$0';
  return n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

function LocationForm({ form, setForm, idPrefix }: { form: any; setForm: any; idPrefix: string }) {
  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f: any) => ({ ...f, [key]: e.target.value }));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="form-control">
          <label htmlFor={`${idPrefix}-name`}>Name</label>
          <input
            id={`${idPrefix}-name`}
            value={form.name}
            onChange={set('name')}
            placeholder="Selma Rail Yard"
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
        <div className="form-control">
          <label htmlFor={`${idPrefix}-line1`}>Line 1 *</label>
          <input
            id={`${idPrefix}-line1`}
            value={form.line1}
            onChange={set('line1')}
            placeholder="404 E Preston St"
          />
        </div>
        <div className="form-control">
          <label htmlFor={`${idPrefix}-line2`}>Line 2</label>
          <input
            id={`${idPrefix}-line2`}
            value={form.line2}
            onChange={set('line2')}
            placeholder="Suite 100"
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 12 }}>
        <div className="form-control">
          <label htmlFor={`${idPrefix}-city`}>City *</label>
          <input id={`${idPrefix}-city`} value={form.city} onChange={set('city')} />
        </div>
        <div className="form-control">
          <label htmlFor={`${idPrefix}-state`}>State *</label>
          <input
            id={`${idPrefix}-state`}
            value={form.state}
            onChange={set('state')}
            placeholder="NC"
          />
        </div>
        <div className="form-control">
          <label htmlFor={`${idPrefix}-postal`}>Postal *</label>
          <input id={`${idPrefix}-postal`} value={form.postalCode} onChange={set('postalCode')} />
        </div>
        <div className="form-control">
          <label htmlFor={`${idPrefix}-country`}>Country *</label>
          <input
            id={`${idPrefix}-country`}
            value={form.country}
            onChange={set('country')}
            placeholder="US"
          />
        </div>
      </div>

      <div className="form-control">
        <label htmlFor={`${idPrefix}-instructions`}>Pickup Instructions</label>
        <textarea
          id={`${idPrefix}-instructions`}
          value={form.pickupInstructions}
          onChange={set('pickupInstructions')}
          rows={6}
          placeholder="Instructions shown in the Ready for Pickup email, e.g. appointment requirements, loading hours, directions..."
          style={{ width: '100%', resize: 'vertical', boxSizing: 'border-box' }}
        />
      </div>

      {/* Rendered bare (no .form-control wrapper) to match the customer
          receiving-hours section exactly — a wrapping .form-control leaks its
          padding/border onto the editor's checkboxes and selects. */}
      <OperatingHoursEditor
        value={form.operatingHours}
        onChange={(next) => setForm((f: any) => ({ ...f, operatingHours: next }))}
        label="Set warehouse hours"
        hint="Shown to carriers as pickup hours on the Bill of Lading and the load email."
      />
    </div>
  );
}

export default function WarehousesTab() {
  const { data: rows = [], isLoading } = useListLocations();
  const createLoc = useCreateLocation();
  const updateLoc = useUpdateLocation();
  const archiveLoc = useArchiveLocation();
  const unarchiveLoc = useUnarchiveLocation();

  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState(EMPTY);
  const [creating, setCreating] = useState(false);

  const [editRow, setEditRow] = useState<any>(null);
  const [editForm, setEditForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const [archivingId, setArchivingId] = useState<string | null>(null);

  const requiredMissing = (obj: any) =>
    ['line1', 'city', 'state', 'postalCode', 'country'].filter((k) => !obj[k]?.toString().trim());

  const openCreate = () => {
    setDraft(EMPTY);
    setShowCreate(true);
  };

  const onCreate = () => {
    const miss = requiredMissing(draft);
    if (miss.length) return toast.warn(`Missing: ${miss.join(', ')}`);
    setCreating(true);
    createLoc.mutate(
      {
        name: draft.name?.trim() || null,
        line1: draft.line1.trim(),
        line2: draft.line2?.trim() || null,
        city: draft.city.trim(),
        state: draft.state.trim(),
        postalCode: draft.postalCode.trim(),
        country: draft.country.trim(),
        deliveryFeeCents: toCents(draft.deliveryFee),
        pickupInstructions: draft.pickupInstructions?.trim() || null,
        operatingHours: draft.operatingHours ?? null,
      },
      {
        onSuccess: () => {
          toast.success('Location created.');
          setShowCreate(false);
          setDraft(EMPTY);
        },
        onError: () => toast.error('Failed to create location.'),
        onSettled: () => setCreating(false),
      }
    );
  };

  const openEdit = (row: any) => {
    if (row.deletedAt) return;
    setEditRow(row);
    setEditForm({
      name: row.name ?? '',
      line1: row.line1 ?? '',
      line2: row.line2 ?? '',
      city: row.city ?? '',
      state: row.state ?? '',
      postalCode: row.postalCode ?? '',
      country: row.country ?? 'US',
      deliveryFee: row.deliveryFeeCents != null ? (row.deliveryFeeCents / 100).toString() : '',
      pickupInstructions: row.pickupInstructions ?? '',
      operatingHours: coerceOperatingHours(row.operatingHours),
    });
  };

  const saveEdit = () => {
    if (!editRow) return;
    const miss = requiredMissing(editForm);
    if (miss.length) return toast.warn(`Missing: ${miss.join(', ')}`);
    setSaving(true);
    updateLoc.mutate(
      {
        id: editRow.id,
        data: {
          name: editForm.name?.trim() || null,
          line1: editForm.line1.trim(),
          line2: editForm.line2?.trim() || null,
          city: editForm.city.trim(),
          state: editForm.state.trim(),
          postalCode: editForm.postalCode.trim(),
          country: editForm.country.trim(),
          deliveryFeeCents: toCents(editForm.deliveryFee),
          pickupInstructions: editForm.pickupInstructions?.trim() || null,
          operatingHours: editForm.operatingHours ?? null,
        },
      },
      {
        onSuccess: () => {
          toast.success('Location updated.');
          setEditRow(null);
        },
        onError: () => toast.error('Failed to update location.'),
        onSettled: () => setSaving(false),
      }
    );
  };

  const toggleArchive = (row: any) => {
    setArchivingId(row.id);
    const isArchived = !!row.deletedAt;
    const m = isArchived ? unarchiveLoc : archiveLoc;
    m.mutate(row.id, {
      onSuccess: () => toast.success(isArchived ? 'Location unarchived.' : 'Location archived.'),
      onError: () => toast.error(`Failed to ${isArchived ? 'unarchive' : 'archive'} location.`),
      onSettled: () => setArchivingId(null),
    });
  };

  return (
    <div className="card">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <div>
          <h2 className="card__title">Warehouses</h2>
          <p className="card__subtitle">Manage pickup &amp; storage locations</p>
        </div>
        <button className="btn btn--primary" onClick={openCreate}>
          Add Location
        </button>
      </div>

      {/* Create modal */}
      <Modal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title="Add Location"
        size="lg"
        footer={
          <>
            <button className="btn btn--ghost" onClick={() => setShowCreate(false)}>
              Cancel
            </button>
            <button className="btn btn--primary" onClick={onCreate} disabled={creating}>
              {creating ? 'Adding...' : 'Add Location'}
            </button>
          </>
        }
      >
        <LocationForm form={draft} setForm={setDraft} idPrefix="create" />
      </Modal>

      {/* Edit modal */}
      <Modal
        isOpen={!!editRow}
        onClose={() => setEditRow(null)}
        title={editRow?.name ? `Edit ${editRow.name}` : 'Edit Location'}
        size="lg"
        footer={
          <>
            <button className="btn btn--ghost" onClick={() => setEditRow(null)}>
              Cancel
            </button>
            <button className="btn btn--primary" onClick={saveEdit} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </>
        }
      >
        <LocationForm form={editForm} setForm={setEditForm} idPrefix="edit" />
      </Modal>

      <div className="card card--bleed-top">
        {isLoading ? (
          <div className="skeleton h-40 w-full" />
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Address</th>
                  <th>Delivery Fee</th>
                  <th>Pickup Instructions</th>
                  <th>Status</th>
                  <th className="ta-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row: any) => {
                  const isArchived = !!row.deletedAt;
                  const address = [row.line1, row.line2, row.city, row.state, row.postalCode]
                    .filter(Boolean)
                    .join(', ');
                  return (
                    <tr key={row.id} style={{ opacity: isArchived ? 0.6 : 1 }}>
                      <td style={{ fontWeight: 500 }}>
                        {row.name || <span className="muted">—</span>}
                      </td>
                      <td className="muted" style={{ fontSize: 13 }}>
                        {address}
                      </td>
                      <td className="muted">{fmtDollarsFromCents(row.deliveryFeeCents)}</td>
                      <td>
                        {row.pickupInstructions ? (
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: 999,
                              fontSize: 12,
                              fontWeight: 600,
                              color: '#1e40af',
                              background: '#dbeafe',
                              border: '1px solid #bfdbfe',
                            }}
                          >
                            Configured
                          </span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: 999,
                            fontSize: 12,
                            fontWeight: 700,
                            color: isArchived ? '#991b1b' : '#065f46',
                            background: isArchived ? '#fee2e2' : '#dcfce7',
                            border: `1px solid ${isArchived ? '#fecaca' : '#bbf7d0'}`,
                          }}
                        >
                          {isArchived ? 'Archived' : 'Active'}
                        </span>
                      </td>
                      <td className="ta-right">
                        <div className="wh-row-actions">
                          <button
                            className="btn btn--ghost"
                            onClick={() => openEdit(row)}
                            disabled={isArchived}
                            title={isArchived ? 'Unarchive to edit' : 'Edit'}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn--ghost"
                            onClick={() => toggleArchive(row)}
                            disabled={archivingId === row.id}
                          >
                            {archivingId === row.id
                              ? isArchived
                                ? 'Unarchiving...'
                                : 'Archiving...'
                              : isArchived
                                ? 'Unarchive'
                                : 'Archive'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="muted ta-center">
                      No locations yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
